import { useEffect, useCallback, useState, useMemo, useLayoutEffect, MutableRefObject } from "react";
// Redux
import { useSelector } from "react-redux";
// Material UI
import { useTheme } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import { Grid, Card, CardContent, Typography, Theme, Box } from "@mui/material";
// Styling
import classNames from "classnames";
import { CrewFilterAndSort, IPositionOptions, ISavedCrewScheduleDisplayOptions } from "./CrewFilterAndSort";
import { CrewScheduleAddEvents } from "./CrewScheduleAddEvents";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { defaultCrewBase, ICrewMember } from "src/redux/reducers/CrewScheduleReducer";
import { OcTabs } from "src/components/OcTabs/OcTabs";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { CrewSearch } from "./CrewSearch";
import { CrewScheduleActions } from "./CrewScheduleActions";
import { CrewScheduleBidAssignment } from "./CrewScheduleBidAssignment";
import { CrewOpenFlights } from "./CrewOpenFlights";
import Tooltip from "@mui/material/Tooltip";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
// Jump To Crew imports
import { useDispatch } from "react-redux";
import {
  toggleSelectedCrewMemberAction,
  setFocusedCrewSchedArea,
  setHideCheckInCheckOutTimes,
  setHideTimeBetweenEvents,
} from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { PositionBadge } from "src/components/CrewSchedule/PositionBadge";
import { formatUserLabel } from "src/components/Admin/Tabs/Users/ManageUsers";
import { SwitchField } from "src/components/FormikComponents/SwitchField";
import { IOpenFlyingSettings, ClientSettingName, IClientSettings } from "@vmosolutions/oc-interfaces";
import { EventModalTypes } from "src/components/DialogModal/DialogModalWrapper";
import { IModalInfo } from "../CrewScheduleWindow";
import { getClientSetting } from "src/redux/actions/App.Actions";
import { useCancellablePromise } from "src/API/useCancellablePromise";
import { hasPermission, PolicyAction, PolicyResource } from "@vmosolutions/oc-interfaces";
import { AnyARecord } from "dns";

/**
 * A helper function to check the crew list in all open crew web windows and
 * return the window ids all or the first one found.
 * @param crewId- id of the crew you are wanting to focus
 * @param filteredCrew- the map of what crew are showing on the crew display
 * @returns
 */
export const getAllCrewSchedWindowIdContainingCrew = (
  crewId: string,
  filteredCrew: {
    [key: string]: ICrewMember[];
  }
): string[] | null => {
  const windowOnTimeline: string[] = [];
  if (!filteredCrew) {
    return null;
  }
  const windowKeys = Object.keys(filteredCrew);
  windowKeys.forEach((key: string) => {
    if (filteredCrew[key]?.findIndex((crew) => crew._id === crewId) >= 0) {
      windowOnTimeline.push(key);
    }
  });
  return windowOnTimeline.length > 0 ? windowOnTimeline : null;
};

/**
 *if you just want the first window id
 * @param crewId-  id of the crew you are wanting to focus
 * @param filteredCrew- the map of what crew are showing on the crew display
 * @returns
 */
export const getFirstCrewSchedWindowIdContainingCrew = (
  crewId: string,
  filteredCrew: {
    [key: string]: ICrewMember[];
  }
): string | null => {
  return getAllCrewSchedWindowIdContainingCrew(crewId, filteredCrew)?.[0];
};

enum Menu {
  SORT = "Crew Filter",
  ADD = "Add Events",
  SEARCH = "Search Crew",
  ACTIONS = "Actions",
  BIDASSIGNMENT = "Bid Assignment",
  OPENFLIGHTS = "Open Flying",
}

interface ICrewScheduleHeaderProps {
  filteredCrew: ICrewMember[];
  handlePositionSelect: (position: IPositionOptions) => void;
  currentPosition: IPositionOptions;
  positionOptions: IPositionOptions[];
  id?: string;
  path?: string[];
  timelineContainer?: MutableRefObject<HTMLDivElement>;
  resizeViewport: (value: boolean | string) => void;
  savedFilters?: ISavedCrewScheduleDisplayOptions; // Loaded from localstorage if available.
  setSavedFilters?: (value: ISavedCrewScheduleDisplayOptions) => void;
  showBidAssignmentTab?: boolean;
  bidAssignModeActive?: boolean;
  setActiveEventModal?: (modalInfo: IModalInfo) => void;
}

export function mimicShiftClickAndScroll(target: any, scrollAmount: number): void {
  const containers = document.querySelectorAll(target);
  containers.forEach((container: any) => {
    const scrollEvent = new WheelEvent("wheel", {
      bubbles: true,
      cancelable: true,
      view: window,
      deltaMode: 0,
      deltaX: scrollAmount,
      deltaY: scrollAmount,
      clientX: (document.documentElement.clientWidth / 2) * window.devicePixelRatio,
      shiftKey: true, //This is needed for the shift + scroll to work
      deltaZ: 0,
    });

    container.dispatchEvent(scrollEvent);
  });
}

const biddingActiveBackgroundcolor = "#075151";

const crewScheduleHeaderStyles = (theme: Theme): any => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

const timelineHeaderMenuStyles = (theme: Theme): any => ({
  display: "flex",
  flexWrap: "nowrap",
  margin: "0px 8px", //to sapce out the tab label
  "& .hovered_flight_preview_wrapper": {
    display: "flex",
    flexShrink: 0,
    borderRadius: "5px",
    width: 128,
    height: 30,
    margin: "0px 8px",
    border: "1px dotted " + theme.custom.forms.labels,
    "& .crewEventChip_container": {
      justifyContent: "center",
      alignContent: "center",
      "& .placeholder_text": {
        textTransform: "uppercase",
        color: theme.custom.forms.labels,
        fontWeight: 300,
        fontSize: 12,
      },
      "& .flightchip": { margin: "3px" },
    },
  },
  // Give bid assignment mode a special code to help indicate it's active to planners.
  "& .MuiTabs-root button.MuiTab-root.active": { backgroundColor: biddingActiveBackgroundcolor },
  // Add text overflow ellipsis to the aircraft sort tab.
  "& .MuiTabs-root button.tab-SORT.MuiTab-wrapped": {
    display: "inline-block",
    maxWidth: 175,
    maxHeight: 24,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
});

const timelineLabelToggleStyles = (theme: Theme): any => ({
  minWidth: "198px",
  display: "flex",
  height: 30,
  margin: "2px",
  padding: "2px",
  "& .toggles_label": {
    margin: "2px 10px 0px 0px",
    width: 40,
    fontFamily: "Roboto, Helvetica, arial, sans-serif",
    fontWeight: 400,
    fontSize: 12,
    textAlign: "right",
    lineHeight: "12px",
  },
  "& .toggle_label": {
    margin: 0,
    width: "auto",
    color: theme.palette.text.primary,
    "&.focused label": { color: "unset" },
    "& label": {
      margin: 0,
      width: 40,
    },
    "& .switch-buttons": {
      margin: "-4px 4px -4px 0px",
      "& button": { height: 12 },
    },
    "&.active": {
      "& .switch-buttons button.disabled": {
        color: "unset",
      },
    },
    "&.display_at_base": {
      marginBottom: "1px",
    },
  },
});

const timelineHeaderContentStyles = (theme: Theme): any => ({
  "& .card": {
    backgroundColor: theme.custom.timeline.contextListBackground,
    "&.BIDASSIGNMENT.active": { backgroundColor: biddingActiveBackgroundcolor },
    boxShadow: theme.shadows[5],
    margin: "0px 8px 8px 8px",
    borderRadius: "0x 4px 4px 4px", // to fix the rounded edge
    "& .MuiCardContent-root": {
      padding: 0,
      "& button": {
        margin: "5px",
        padding: "2px 5px",
        fontSize: 12,
        fontWeight: 300,
        "&.find_previous, &.find_next": {
          padding: 0,
        },
      },
      "& h6.selection_label": {
        display: "inline-flex",
        margin: "5px",
        padding: "2px",
        height: 24,
        lineHeight: "20px",
      },
    },
  },
});

const defaultOpenFlightSettings: IOpenFlyingSettings = {
  enabled: false,
  crewScheduleEmails: [],
  crewAssignmentSettings: {
    defaultBaseICAO: null,
    defaultStartHoursBack: 168, //7 days
    defaultEndHoursForward: 168, //7 days
  },
  crewWebSettings: {
    hoursBack: 168, //7 days
    hoursForward: 168, //7 days
  },
  requestSortByDate: false,
};

/**
 * Component created to dynamically render content based on selected tabs in the timeline header menu.
 * @param props -
 * @param state -
 */
export const CrewScheduleHeader = (props: ICrewScheduleHeaderProps): any => {
  const {
    filteredCrew,
    resizeViewport,
    id,
    path,
    handlePositionSelect,
    currentPosition,
    positionOptions,
    timelineContainer,
    savedFilters,
    setSavedFilters,
    showBidAssignmentTab,
    bidAssignModeActive,
    setActiveEventModal,
  } = props;
  const theme = useTheme();
  const { cancellablePromise } = useCancellablePromise();

  const [tabValue, setTabValue] = useState<boolean | string>(false);
  // Context menu management
  const contextMenuMap = useSelector((state: IRootState) => state.contextMenu.menus);
  const windowProperties = useSelector((state: IRootState) => state.crewSchedule.windowProperties[id]);
  const crewBase = windowProperties?.crewBase ?? defaultCrewBase;
  //const displayTimezone = windowProperties?.displayTimezone ?? defaultTimezone;

  const isContextMenuOpen = contextMenuMap.get(id)?.isOpen; // retrieve menu state.
  const menuElement = document.getElementById(`${id}-contextmenu`);
  const [contextWidth, setContextWidth] = useState<number>(0);
  const crewGroupFilter: string[] | undefined = useSelector((state: IRootState) => state.crewSchedule.groupFilter[id]);

  const [openFlightSettings, setOpenFlightSettings] = useState<IOpenFlyingSettings>(defaultOpenFlightSettings);
  useEffect(() => {
    const getOpenFlightSetting = async (): Promise<void> => {
      const openFlyingSetting: IClientSettings = await cancellablePromise(
        //@ts-ignore
        getClientSetting(ClientSettingName.OpenFlying ?? "openFlying")
      );
      if (openFlyingSetting?.setValue) {
        setOpenFlightSettings({ ...defaultOpenFlightSettings, ...openFlyingSetting?.setValue });
      }
    };
    getOpenFlightSetting();
    //! We do not want the cancellablePromise hook to cause a re-render so leave it out of the dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const policies = useSelector((state: IRootState) => state.authentication?.attributes?.policies);
  const enabledOpenFlying = useMemo(
    () => openFlightSettings?.enabled && hasPermission(policies, PolicyResource.OpenFlying, PolicyAction.Update),
    [policies, openFlightSettings?.enabled]
  );

  // Bidding related checks
  // const bidAssignmentIsDirty = useSelector((state: IRootState) => state.bidding.assignmentIsDirty);

  const hideCheckInCheckOutTimes = useMemo(() => {
    return windowProperties?.hideCheckInCheckOutTimes !== undefined ? windowProperties?.hideCheckInCheckOutTimes : true;
  }, [windowProperties]);

  const hideTimeBetweenEvents = useMemo(() => {
    return windowProperties?.hideTimeBetweenEvents !== undefined ? windowProperties?.hideTimeBetweenEvents : true;
  }, [windowProperties]);

  const toggleHideCheckInCheckOutTimes = (): void => {
    dispatch(setHideCheckInCheckOutTimes(id, !hideCheckInCheckOutTimes));
  };

  const toggleHideTimeBetweenEvents = (): void => {
    dispatch(setHideTimeBetweenEvents(id, !hideTimeBetweenEvents));
  };

  useEffect(() => {
    // There's an active bid assignment loaded, force bid assignment tab open.
    if (bidAssignModeActive && tabValue !== "BIDASSIGNMENT") setTabValue("BIDASSIGNMENT");
  }, [bidAssignModeActive, tabValue]);

  useLayoutEffect(() => resizeViewport && resizeViewport(tabValue), [resizeViewport, tabValue]);

  // Use effect fired off if user opens the context menu.
  // Returns the actual width of the menu after a slight delay.
  useEffect(() => {
    const menuWidthTimer = setTimeout(() => {
      const menuWidth =
        isContextMenuOpen && menuElement?.firstElementChild ? menuElement.firstElementChild.clientWidth : 0;
      setContextWidth(menuWidth);
    }, 100);
    return () => clearTimeout(menuWidthTimer);
  }, [isContextMenuOpen, menuElement, id]);

  // Jump to Crew Menu
  const dispatch = useDispatch();
  const selectedCrewDays = useSelector((state: IRootState) => state.crewSchedule.selectedCrewDays);
  const selectedCrew = useMemo(() => {
    return filteredCrew?.find((crew: ICrewMember) => crew._id === selectedCrewDays?.crewId || crew.isSelected);
  }, [filteredCrew, selectedCrewDays]);

  // Set max height for the included jump to crew menu.
  const maxCrewMenuHeight = timelineContainer?.current?.getBoundingClientRect
    ? timelineContainer?.current?.getBoundingClientRect().height - 50
    : 400;

  const handleSelectCrew = useCallback(
    (crew: ICrewMember) => {
      // If being used in bidding mode and assignment is dirty, open modal.

      // If bidAssignment mode is active, force expansion of crew row upon selection but disable context.
      dispatch(toggleSelectedCrewMemberAction(crew?._id, !bidAssignModeActive, bidAssignModeActive));

      if (crew?._id) {
        dispatch(setFocusedCrewSchedArea({ timeInterval: [], crewIds: [crew._id] }, id));
      }
    },
    [bidAssignModeActive, dispatch, id]
  );

  /**
   * Wrapper function for the menu tabs. Place any repeating code here.
   * Timeline functions are passed in as props and applied to the components with spread operators.
   * @param index -
   */
  const tabContent = useCallback((): any => {
    if (!!tabValue && typeof tabValue === "string") {
      switch (tabValue) {
        case "SORT":
          return (
            <CrewFilterAndSort
              currentPosition={currentPosition?.label}
              positionHandler={handlePositionSelect}
              positionOptions={positionOptions}
              windowId={id}
              savedFilters={savedFilters}
              setSavedFilters={setSavedFilters}
              setTabValue={setTabValue}
            />
          );
        case "ADD":
          return (
            <CrewScheduleAddEvents
              timelineContainer={timelineContainer.current}
              windowId={id}
              path={path}
              setTabValue={setTabValue}
            />
          );
        case "FIND":
          if (!filteredCrew) {
            return null;
          }
          return (
            <CrewSearch
              currentPosition={currentPosition?.label}
              positionHandler={handlePositionSelect}
              timelineContainer={timelineContainer.current}
              windowId={id}
              setTabValue={setTabValue}
            />
          );
        case "ACTIONS":
          return <CrewScheduleActions windowId={id} setTabValue={setTabValue} />;
        case "BIDASSIGNMENT":
          return (
            <CrewScheduleBidAssignment
              timelineContainer={timelineContainer.current}
              windowId={id}
              path={path}
              filteredCrew={filteredCrew}
              bidAssignModeActive={bidAssignModeActive}
              setActiveEventModal={setActiveEventModal}
              setTabValue={setTabValue}
            />
          );
        case "OPENFLIGHTS":
          return enabledOpenFlying ? (
            <CrewOpenFlights windowId={id} openFlightSettings={openFlightSettings} setTabValue={setTabValue} />
          ) : (
            <></>
          );
        default:
          return null;
      }
    }
    return null;
  }, [
    currentPosition?.label,
    filteredCrew,
    handlePositionSelect,
    id,
    path,
    positionOptions,
    savedFilters,
    setSavedFilters,
    tabValue,
    timelineContainer,
    bidAssignModeActive,
    setActiveEventModal,
    openFlightSettings,
    enabledOpenFlying,
  ]);

  const handleSelectTab = useCallback(
    (event: any, newValue: any): void => {
      if (bidAssignModeActive) {
        setActiveEventModal({
          type: EventModalTypes.BidAssignmentDirty,
          modalProps: {
            title: "Tabs Locked",
            message: "Please exit bid assignment mode to reactivate tabs.",
          },
        });
        return;
      }
      const value = newValue === tabValue ? false : newValue;
      setTabValue(value);
    },
    [bidAssignModeActive, tabValue, setActiveEventModal]
  );

  const crewFilterLabel = useMemo(() => {
    //const crewGroupLabel = crewGroupFilter?.length > 0 ? ` ${crewGroupFilter.join(", ")}` : "";
    const groupLabel = crewGroupFilter?.length === 1 ? crewGroupFilter[0] : "";
    return crewGroupFilter?.length > 1
      ? `${Menu.SORT}: ${crewBase?.key ?? ""} ${currentPosition?.label ?? ""}s From Multiple Groups`
      : `${Menu.SORT}: ${crewBase?.key ?? ""} ${groupLabel} ${currentPosition?.label ?? ""}s`;
  }, [crewBase?.key, crewGroupFilter, currentPosition?.label]);

  const isMultipleGroups = crewGroupFilter?.length > 1;

  return (
    <Grid sx={crewScheduleHeaderStyles(theme)} container alignContent="flex-start">
      <Grid item sx={{ ...timelineHeaderMenuStyles(theme), width: `calc(100% - ${contextWidth}px)` }}>
        <Grid item style={{ width: 250, minWidth: 250 }}>
          <SelectField
            className="jump_to_crew"
            name="jumpToCrew"
            label=""
            maxMenuHeight={maxCrewMenuHeight}
            isMulti={false}
            value={selectedCrew}
            onChange={handleSelectCrew}
            options={filteredCrew}
            getOptionLabel={formatUserLabel}
            formatOptionLabel={(user: any) => {
              const { firstName, lastName, employeeId, crewInfo } = user as ICrewMember;
              const highPos = crewInfo?.positions?.sort((a, b) => a.displayOrder - b.displayOrder)[0];
              return (
                <Grid container className="crew_option_container" alignItems="center" wrap="nowrap">
                  {highPos && <PositionBadge position={highPos} filled={true} isInactive={!crewInfo?.isActive} />}
                  <Grid item style={{ marginLeft: 10 }}>{`${firstName} ${lastName} ${
                    employeeId ? `[${employeeId}]` : ""
                  }`}</Grid>
                </Grid>
              );
            }}
            getOptionValue={(user: ICrewMember) => user._id}
            placeholder="Jump To Filtered Crew"
            isSearchable={true}
            isClearable={true}
            noOptionsMessage={() => "Selected filter returns no crew"}
          />
        </Grid>
        <Box sx={timelineLabelToggleStyles(theme)}>
          <Typography className="toggles_label">REST CI/CO</Typography>
          <div className="switch_fields">
            <SwitchField
              className={`display_at_base toggle_label ${!hideTimeBetweenEvents && "active"}`}
              name="rest"
              value={hideTimeBetweenEvents}
              onChange={toggleHideTimeBetweenEvents}
              switchlabels={["HIDE", "SHOW"]}
              switchtooltips={["Hide Rest", "Show Rest"]}
              label=""
              allowNull={false}
            />
            <SwitchField
              className={`display_ci_co toggle_label ${!hideCheckInCheckOutTimes && "active"} `}
              name="CI/CO times"
              value={hideCheckInCheckOutTimes}
              onChange={toggleHideCheckInCheckOutTimes}
              switchlabels={["HIDE", "SHOW"]}
              switchtooltips={["Hide Check-In/Check-Out Times", "Show Check-In/Check-Out Times"]}
              label=""
              allowNull={false}
            />
          </div>
          <Box sx={{ ml: 1, display: "flex", justifyContent: "end", alignItems: "center" }}>
            <Tooltip title="Zoom Schedule Out">
              <ZoomOutIcon
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  mimicShiftClickAndScroll(".crew_schedGL", 200);
                }}
              />
            </Tooltip>
            <Tooltip title="Zoom Schedule In">
              <ZoomInIcon sx={{ cursor: "pointer" }} onClick={() => mimicShiftClickAndScroll(".crew_schedGL", -200)} />
            </Tooltip>
          </Box>
        </Box>
        <OcTabs variant="scrollable" scrollButtons="auto" value={tabValue} onChange={handleSelectTab}>
          <Tab
            wrapped={true}
            value="SORT"
            label={
              isMultipleGroups && !bidAssignModeActive ? (
                <Tooltip title={`Groups: ${crewGroupFilter.join(", ")}`} enterDelay={1000} placement="top">
                  <div>{crewFilterLabel}</div>
                </Tooltip>
              ) : bidAssignModeActive ? (
                "Crew Filter: Bid Package"
              ) : (
                crewFilterLabel
              )
            }
          />
          <Tab wrapped={true} value="ADD" label={`${Menu.ADD}`} />
          <Tab wrapped={true} value="FIND" label={`${Menu.SEARCH}`} />
          <Tab wrapped={true} value="ACTIONS" label={`${Menu.ACTIONS}`} />
          {enabledOpenFlying && <Tab wrapped={true} value="OPENFLIGHTS" label={`${Menu.OPENFLIGHTS}`} />}
          {showBidAssignmentTab && (
            <Tab
              wrapped={true}
              value="BIDASSIGNMENT"
              label={`${Menu.BIDASSIGNMENT}${bidAssignModeActive ? ": Active" : ""}`}
              className={bidAssignModeActive ? "active" : ""}
            />
          )}
        </OcTabs>
      </Grid>

      <Grid item sx={{ ...timelineHeaderContentStyles(theme), width: `calc(100% - ${contextWidth}px)` }}>
        <Card
          classes={{
            root: classNames("card", tabValue, tabValue === "BIDASSIGNMENT" && bidAssignModeActive ? "active" : ""),
          }}
        >
          <CardContent>{tabContent()}</CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};
